import React, { useEffect } from 'react';
import styles from './MostUsedKnowledgeBase.module.scss';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import * as XLSX from 'xlsx';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import Loader from 'component/atom/loader/Loader';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';

const MostUsedKnowledgeBase = ({
  id,
  startDate,
  endDate,
  skipFetch = false,
  snapshotMode,
  setLoader,
  botType,
}) => {
  const { data, refetch, isFetching } = useFetch(
    ['Most-Used-KnowledgeBase', startDate, endDate, id, botType],
    dashboardEndpoints.getKnowledgeBase(startDate, endDate, id),
    { enabled: true },
    id ? { botId: id, chatSource: botType } : {}
  );

  const handleDownload = () => {
    const rows = Array.isArray(data?.data) ? data.data : [];

    const tableData = rows.map((r) => {
      const { label, href } = parseMdLink(r.title);

      return {
        'KB Item': href ? `${label} (${href})` : label ?? '',
        Type: r.type ?? '',
        'Usage Count':
          typeof r.count === 'number' ? r.count : Number(r.count ?? 0),
      };
    });

    const ws = XLSX.utils.json_to_sheet(tableData, {
      header: ['KB Item', 'Type', 'Usage Count'],
    });

    const colHeaders = ['KB Item', 'Type', 'Usage Count'];
    const colWidths = colHeaders.map((h) => Math.max(10, String(h).length + 2));

    tableData.forEach((row) => {
      colHeaders.forEach((key, idx) => {
        const val = row[key];
        const len = String(val ?? '').length + 2;
        if (len > colWidths[idx]) colWidths[idx] = len;
      });
    });

    ws['!cols'] = colWidths.map((wch) => ({ wch }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Most Used');

    const suffix = [
      startDate ? `from_${startDate}` : null,
      endDate ? `to_${endDate}` : null,
      id ? `bot_${id}` : null,
    ]
      .filter(Boolean)
      .join('_');

    const filename = `most_used_knowledge_base${
      suffix ? `_${suffix}` : ''
    }.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  useEffect(() => {
    if (skipFetch || botType === undefined) return;
    refetch();
  }, [startDate, endDate, skipFetch, refetch, id, botType, id]);
  useEffect(() => {
    if (setLoader) setLoader(isFetching);
  }, [isFetching, setLoader]);

  const parseMdLink = (title) => {
    if (!title) return { label: '', href: null };

    const open = title.indexOf('(');
    const close = title.lastIndexOf(')');

    if (open === -1 || close === -1 || close <= open) {
      return { label: title, href: null };
    }

    const label = title.slice(0, open).trim();
    let href = title.slice(open + 1, close).trim();

    if (!/^https?:\/\//i.test(href)) {
      href = `https://${href}`;
    }

    return { label, href };
  };

  return (
    <div className={styles['card']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <div
            className={`${styles['title']} ${
              snapshotMode && styles['snapshotMode-title']
            }`}
          >
            <p>Most-Used Knowledge Base</p>
            {!snapshotMode && (
              <Tooltip
                content='Shows the most frequently accessed knowledge
base files or URLs within the selected timeframe.'
                className={styles['tooltip']}
              >
                <SvgWithBranding type='info' color='#682ECB' width={22} />
              </Tooltip>
            )}
          </div>
          {!snapshotMode && (
            <SvgWithBranding
              className={styles['download-icon']}
              type='download2'
              onClick={handleDownload}
            />
          )}
        </div>
      </div>
      {snapshotMode && (
        <p className='snap-desc'>
          Shows the most frequently accessed knowledge base files or URLs within
          the selected timeframe.
        </p>
      )}
      <div className={styles['table-wrap']}>
        <div className={styles['table-header']}>
          <div className={styles['th-item']}>KB Item</div>
          <div className={styles['th-type']}>Type</div>
          <div className={styles['th-count']}>Usage Count</div>
        </div>

        <div className={styles['table-body']}>
          {isFetching ? (
            <Loader />
          ) : data?.data?.length > 0 ? (
            data?.data?.slice(0, 5).map((r, idx) => {
              const { label, href } = parseMdLink(r.title);
              return (
                <div key={idx} className={styles['tr']}>
                  <div className={styles['td-item']}>
                    {href ? (
                      <a
                        href={href}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={styles['kb-link']}
                      >
                        {label}
                      </a>
                    ) : (
                      label
                    )}
                  </div>
                  <div className={styles['td-type']}>{r.type}</div>
                  <div className={styles['td-count']}>{r.count}</div>
                </div>
              );
            })
          ) : (
            <NoData containerStyle={{ marginTop: '50px' }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MostUsedKnowledgeBase;
